import { ref } from 'vue';
import { api } from '@/services/api';
import { DateTime } from 'luxon';
import { requestMarkAsCompleted } from '@/services/missions';

export function usePreventionParcours(baseUrl: string) {
  const data = ref<Record<string, any> | null>(null);
  const loading = ref(false);

  const getById = async (id: number | string) => {
    loading.value = true;
    try {
      const response = await api.get<Record<string, any>>(`${baseUrl}/${id}`, {
        params: { filter_field: 'reservation_id' },
      });
      data.value = response.data;
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const flattenPayload = (payload: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([_, v]) => typeof v !== 'object' || v === null || v instanceof Date,
      ),
    );
  };

  // `markCompleted: false` quand l'appelant passe lui-même la réservation en
  // COMPLETED (plusieurs updates d'affilée) : un 2e PUT identique dans la même
  // seconde ne modifie aucune ligne et l'API répond 404.
  const update = async (
    id: number | string,
    payload: Record<string, any>,
    isSendPdf = true,
    markCompleted = true,
  ) => {
    loading.value = true;
    try {
      const response = await api.put<Record<string, any>>(
        `${baseUrl}/${id}`,
        flattenPayload({ ...payload, completed_at: DateTime.now() }),
      );
      data.value = response.data;

      // Fallback sur l'id de route (flows vaccination) : sans réservation, PUT
      // /cms/reservation/undefined → 400 et le PDF n'est jamais demandé.
      if (markCompleted) {
        const reservationId = payload.reservation_id ?? payload._reservationId;
        if (reservationId) {
          await requestMarkAsCompleted(reservationId);
        } else {
          console.warn(`[usePreventionParcours] reservation_id absent pour ${baseUrl}/${id}`);
        }
      }

      if (isSendPdf) {
        await api.post('/cms/send-pdf', {
          id,
          type: 'parcoursPrevention',
          resource: baseUrl.replace('/cms/', ''),
        });
      }

      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      loading.value = false;
    }
  };

  return { data, loading, getById, update };
}
